'use client';

import { Flex, type FlexProps } from '@ironhack/design-system2/components/flex';
import { Box } from '@ironhack/design-system2/components/box';
import { Text } from '@ironhack/design-system2/components/text';
import { useBreakpointValue } from '@ironhack/design-system2/hooks';
import { type ReactElement, useContext, useEffect, useState } from 'react';

import { useIsClient } from '@/app/components/client/client-only';
import { Link } from '@/app/components/client/link';
import { NavItemIcon } from '../nav-item-icon';
import { EXCEPTION_MEDIA_QUERY } from '../nav-menu';
import { SecondLevelMenu } from '../second-level-menu';
import { type TopLevelItem, isTarget } from '../types';
import { NavbarContext } from '../navbar-context';
import { usePageContext } from '@/hooks';
import { sendEvent } from '@/lib/gtm';

type Props = {
  menuItems: TopLevelItem[];
};

const TopLevelItemWrapper = (flexProps: FlexProps): ReactElement => (
  <Flex
    align="center"
    gap={[null, null, null, 0.5]}
    justify={['space-between', null, null, 'normal']}
    sx={{
      [EXCEPTION_MEDIA_QUERY]: { gap: 0.25 },
    }}
    {...flexProps}
  />
);

export const TopLevelMenu = (props: Props): ReactElement => {
  const { menuItems } = props;
  const isHamburgerMenu = useBreakpointValue({ base: true, lg: false });
  const isClient = useIsClient();

  const { isFirstLevel, resetMenuLevel, setSecondLevel } =
    useContext(NavbarContext);

  const [secondLevelIndex, setSecondLevelIndex] = useState(-1);

  const {
    pageData: { gaPageType },
  } = usePageContext();

  useEffect(() => {
    if (isFirstLevel) setSecondLevelIndex(-1);
  }, [isFirstLevel, setSecondLevelIndex]);

  return (
    <>
      {menuItems.map((item: TopLevelItem, index: number): ReactElement => {
        const isSelected = index === secondLevelIndex;
        return (
          <Box
            _last={{
              borderLeft: [null, null, null, '1px solid'],
              pl: [null, null, null, 5],
            }}
            as="li"
            data-level="1"
            display={[
              isFirstLevel || isSelected ? 'block' : 'none',
              null,
              null,
              'block',
            ]}
            key={`top-level-item-${item.id}`}
            sx={{
              [EXCEPTION_MEDIA_QUERY]: {
                _last: { pl: 3 },
              },
            }}
          >
            {isTarget(item.content) ? (
              <TopLevelItemWrapper>
                <Link withoutDecoration {...item.content.target}>
                  {item.label}
                </Link>
                {/* On large screens no icon for top level links */}
                {isClient && isHamburgerMenu && (
                  <NavItemIcon boxSize="24px" itemType="link" />
                )}
              </TopLevelItemWrapper>
            ) : (
              <>
                <TopLevelItemWrapper
                  display={isSelected ? ['none', null, null, 'flex'] : 'flex'}
                  onClick={() => {
                    sendEvent({
                      eventAction: 'nav menu_level 1',
                      eventCategory: 'Nav',
                      eventLabel: item.content.eventLabel,
                      pageType: gaPageType,
                    });
                    if (isSelected) {
                      setSecondLevelIndex(-1);
                      resetMenuLevel();
                    } else {
                      setSecondLevelIndex(index);
                      setSecondLevel();
                    }
                  }}
                  role="button"
                >
                  <Text
                    // Avoid layout shift when selected & has larger, bold style
                    _before={{
                      content: [null, null, null, `"${item.label}"`],
                      display: [null, null, null, 'block'],
                      height: [null, null, null, 0],
                      overflow: [null, null, null, 'hidden'],
                      textStyle: [null, null, null, 'mBold'],
                      visibility: [null, null, null, 'hidden'],
                    }}
                    {...(isSelected && {
                      textStyle: ['m', null, null, 'mBold'],
                    })}
                  >
                    {item.label}
                  </Text>
                  <NavItemIcon
                    boxSize={['24px', null, null, '16px']}
                    // On large screens top level menu icon points down
                    iconDirection={
                      isClient && isHamburgerMenu ? 'right' : 'down'
                    }
                    itemType="menu"
                  />
                </TopLevelItemWrapper>
                <SecondLevelMenu
                  isSelected={isSelected}
                  menu={item.content}
                  title={item.label}
                />
              </>
            )}
          </Box>
        );
      })}
    </>
  );
};
